import { apiService } from '@/shared/services/api/api.service';

export interface DocumentType {
  TERMS_OF_SERVICE: string;
  INDEPENDENT_CONTRACTOR_AGREEMENT: string;
  TAX_FORM_W9: string;
  TAX_FORM_1099: string;
  DIRECT_DEPOSIT_FORM: string;
  LEGAL_AGREEMENT: string;
  COMPLIANCE_DOCUMENT: string;
  OTHER: string;
}

export interface DocumentStatus {
  PENDING: string;
  VIEWED: string;
  IN_PROGRESS: string;
  SIGNED: string;
  COMPLETED: string;
  REJECTED: string;
}

export interface Document {
  id: string;
  name: string;
  description?: string;
  type: string;
  isRequired: boolean;
  status: 'PENDING' | 'VIEWED' | 'IN_PROGRESS' | 'SIGNED' | 'COMPLETED';
  signedAt?: string;
  displayOrder: number;
  documentUrl?: string;
  viewedAt?: string;
  // DocuSign integration fields
  useDocuSign?: boolean;
  docusignTemplateId?: string;
  docusignEnvelopeId?: string; // Added for polling
  docusignStatus?: string;
  docusignSigningUrl?: string;
}

export interface DocumentCompletionStatus {
  totalDocuments: number;
  completedDocuments: number;
  pendingDocuments: number;
  completionPercentage: number;
  documents: Document[];
}

export interface SignatureData {
  fullName: string;
  initials?: string;
  timestamp?: string;
}

class DocumentService {
  /**
   * Get all documents for the current user
   */
  async getMyDocuments(): Promise<{ success: boolean; data: DocumentCompletionStatus }> {
    const response = await apiService.get<{ success: boolean; data: DocumentCompletionStatus }>(
      '/documents/my-documents'
    );
    return response.data;
  }

  /**
   * Mark a document as viewed
   */
  async markDocumentViewed(documentId: string): Promise<{ success: boolean }> {
    const response = await apiService.post<{ success: boolean }>(
      `/documents/${documentId}/viewed`
    );
    return response.data;
  }

  /**
   * Sign a single document
   */
  async signDocument(
    documentId: string,
    signatureData: SignatureData
  ): Promise<{ success: boolean; message?: string }> {
    const response = await apiService.post<{ success: boolean; message?: string }>(
      `/documents/${documentId}/sign`,
      { signatureData }
    );
    return response.data;
  }

  /**
   * Sign multiple documents at once
   */
  async bulkSignDocuments(
    documentIds: string[],
    signatureData: SignatureData
  ): Promise<{ success: boolean; data: { signed: number; failed: number }; message?: string }> {
    const response = await apiService.post<{
      success: boolean;
      data: { signed: number; failed: number };
      message?: string;
    }>('/documents/bulk-sign', { documentIds, signatureData });
    return response.data;
  }

  /**
   * Get a specific document
   */
  async getDocument(documentId: string): Promise<{ success: boolean; data: any }> {
    const response = await apiService.get<{ success: boolean; data: any }>(
      `/documents/${documentId}`
    );
    return response.data;
  }
}

export const documentService = new DocumentService();
